import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import * as Sentry from '@sentry/angular';
import * as SentryCapacitor from '@sentry/capacitor';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SentryService {

  constructor(private platform: Platform) {}

  async initSentry(): Promise<void> {
    if (this.platform.is('capacitor')) {
      // Initialize Sentry for Capacitor (native mobile)
      await SentryCapacitor.init({
        dsn: environment.sentry.dsn,
        environment: environment.sentry.environment,
        debug: !environment.production,

        // Performance monitoring
        tracesSampleRate: environment.production ? 0.1 : 1.0,

        // Session replay - Optimized for performance
        replaysSessionSampleRate: environment.production ? 0.05 : 0.1,
        replaysOnErrorSampleRate: environment.production ? 0.5 : 1.0,

        // Release management
        beforeSend(event) {
          // Optionally filter events before sending
          if (event.exception && !environment.production) {
            console.log('Sentry Event:', event);
          }
          return event;
        }
      });

      // Set tags after initialization
      SentryCapacitor.setTag('platform', 'capacitor');
      SentryCapacitor.setTag('app', 'lisaas-voorraad');
      SentryCapacitor.setTag('version', '3.3.26');
    }
  }

  // Utility methods for manual error reporting
  captureException(error: Error, context?: any): void {
    if (this.platform.is('capacitor')) {
      if (context) {
        SentryCapacitor.withScope((scope) => {
          scope.setContext('additional_context', context);
          SentryCapacitor.captureException(error);
        });
      } else {
        SentryCapacitor.captureException(error);
      }
    } else {
      if (context) {
        Sentry.withScope((scope) => {
          scope.setContext('additional_context', context);
          Sentry.captureException(error);
        });
      } else {
        Sentry.captureException(error);
      }
    }
  }

  captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
    if (this.platform.is('capacitor')) {
      SentryCapacitor.captureMessage(message, level);
    } else {
      Sentry.captureMessage(message, level);
    }
  }

  setUser(user: { id?: string; email?: string; username?: string }): void {
    if (this.platform.is('capacitor')) {
      SentryCapacitor.setUser(user);
    } else {
      Sentry.setUser(user);
    }
  }

  setTag(key: string, value: string): void {
    if (this.platform.is('capacitor')) {
      SentryCapacitor.setTag(key, value);
    } else {
      Sentry.setTag(key, value);
    }
  }

  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    if (this.platform.is('capacitor')) {
      SentryCapacitor.addBreadcrumb(breadcrumb);
    } else {
      Sentry.addBreadcrumb(breadcrumb);
    }
  }
}